import { useContext, useMemo } from "react";
import {
  aggregateTextInlineStyle,
  textBaseStyleOf,
  type TextInlineStylePatch,
} from "@refinement-canvas/domain";
import { useCanvasStore } from "../store/canvas-store";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";
import { readTextRangeBold, toggleTextRangeBold } from "../text-range-bold";
import { SelectionPanelPlacementContext } from "./selection-style-panel-frame";
import { TextRangeStylePanelFrame } from "./text-range-style-panel-frame";
import { ToolbarParentChildPanel } from "./toolbar-parent-child-panel";
import {
  renderTextRangeStyleChild,
  textRangeStyleParentItems,
  TextStyleBoldButton,
  type TextRangeStyleChildInput,
  type TextRangeStyleItemKey,
} from "./text-style-panel-model";
import { DEFAULT_TEXT_FONT_SIZE } from "../../../lib/text-size-preference";
import {
  normalizeTextColorForCompare,
  normalizeTextFillForCompare,
  TEXT_FILL_TRANSPARENT,
} from "../../../lib/text-color";
import type { CustomColorPalettes } from "./use-custom-color-palettes";

// テキスト図形を編集中に、非空な文字範囲を選択したときだけ出る範囲専用の書式パネル。
//
// - 項目は全文用パネルから文字寄せだけを除いた「文字の色 → 背景の色 → 文字サイズ → 太字」。
//   順序・ラベル・選択肢は text-style-panel-model を共有の正本とする。
// - 現在値は「文字ごとの override ?? 図形の基底書式」で集計し、2 値以上あれば混在表示にする。
// - 適用対象は選択範囲の可視文字だけで、Markdown の隠しプレフィックス・改行には run を作らない
//   （判定は domain の共有関数が行う）。

interface Props {
  readonly palettes: CustomColorPalettes;
}

export function TextRangeStylePanel({ palettes }: Props): JSX.Element | null {
  const range = useCanvasStore((s) => s.activeTextRangeSelection);
  const readOnly = useCanvasStore((s) => s.readOnly);
  const objects = useCanvasDocStore((s) => s.objects);
  const notifyTextRangeStyleApplied = useCanvasStore((s) => s.notifyTextRangeStyleApplied);
  const dispatcher = useCanvasOpDispatcherContext();

  const target = range ? objects[range.objectId] : undefined;
  const object = target?.type === "text" && target.locked !== true ? target : null;

  const values = useMemo(() => {
    if (!object || !range) return null;
    const aggregate = aggregateTextInlineStyle({
      base: textBaseStyleOf(object),
      end: range.end,
      runs: object.inlineStyles,
      start: range.start,
      text: object.text,
    });
    return {
      // chooser は「混在は null、それ以外は描画時と同じ実効値」を期待するため、
      // 未設定の項目は全文用パネルと同じ既定値へ寄せる。
      color: aggregate.color.mixed ? null : normalizeTextColorForCompare(aggregate.color.value),
      fill: aggregate.fill.mixed ? null : normalizeTextFillForCompare(aggregate.fill.value),
      fontSize: aggregate.fontSize.mixed
        ? null
        : (aggregate.fontSize.value ?? DEFAULT_TEXT_FONT_SIZE),
      // 太字だけはショートカット（本文編集中の Ctrl+B）と同じ共有関数で読む。表示と操作の
      // 判定を 1 か所へ寄せるため、集計が 1 レンダーで 2 回走ることは受け入れる（対象は
      // 選択文字数ぶんで、体感できる負荷ではない）。
      bold: readTextRangeBold(object, { end: range.end, start: range.start }),
    };
  }, [object, range]);

  if (readOnly) return null;
  if (!range || !object || !values) return null;

  const applyPatch = (patch: TextInlineStylePatch): void => {
    const applied = dispatcher.updateTextRangeStyle(
      object.id,
      { start: range.start, end: range.end, text: range.text },
      patch
    );
    // 編集中の Ctrl+Z はエディタ自前のスナップショットスタックが正本なので、成立した書式適用を
    // エディタへ通知して「変更前の run」を履歴へ積ませる（適用直後の Undo が直前の入力へ
    // 消費されるのを防ぐ）。送信しなかった場合は履歴も積まない。
    if (applied) notifyTextRangeStyleApplied();
  };
  const childInput: TextRangeStyleChildInput = {
    testIdPrefix: "text-range-style",
    ariaLabelPrefix: "選択した文字の",
    palettes,
    color: values.color,
    fill: values.fill,
    fontSize: values.fontSize,
    // 任意色の追加は行わない（ポップオーバーへフォーカスが移ると選択範囲を見失うため）。
    allowCustomColorEditing: false,
    onChooseColor: (color) => applyPatch({ color }),
    // 「背景なし」は個別ハイライトの除去（override の削除）であり、図形の基底背景は残す。
    onChooseFill: (fill) => applyPatch({ fill: fill === TEXT_FILL_TRANSPARENT ? null : fill }),
    onChooseFontSize: (px) => applyPatch({ fontSize: px }),
  };

  return (
    <TextRangeStylePanelFrame>
      <TextRangeStylePanelBody
        childInput={childInput}
        bold={values.bold}
        // 混在状態を押したときは全範囲を太字へそろえる（規則は共有関数が持つ）。
        onToggleBold={() => {
          const applied = toggleTextRangeBold({
            dispatcher,
            objectId: object.id,
            range: { end: range.end, start: range.start, text: range.text },
          });
          if (applied) notifyTextRangeStyleApplied();
        }}
      />
    </TextRangeStylePanelFrame>
  );
}

/** 親子パネルの組み立て（配置側の情報を子の端揃えへ渡すためフレームの内側で読む）。 */
function TextRangeStylePanelBody({
  childInput,
  bold,
  onToggleBold,
}: {
  readonly childInput: TextRangeStyleChildInput;
  readonly bold: boolean | null;
  readonly onToggleBold: () => void;
}): JSX.Element {
  const parentSide = useContext(SelectionPanelPlacementContext)?.side ?? "bottom";
  const items = textRangeStyleParentItems({
    color: childInput.color,
    fill: childInput.fill,
    fontSize: childInput.fontSize,
  });
  return (
    <ToolbarParentChildPanel<TextRangeStyleItemKey>
      label="選択した文字のスタイル"
      items={items}
      renderChild={(key) => renderTextRangeStyleChild(key, childInput)}
      parentSide={parentSide}
      testIdPrefix="text-range-style"
      directActions={
        <TextStyleBoldButton
          pressed={bold}
          testId="text-range-style-bold"
          onToggle={onToggleBold}
        />
      }
    />
  );
}
